import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { ArrowLeft, ShieldCheck, Info, PlugZap, RefreshCw, PowerOff, Loader2, QrCode, RefreshCcw, KeyRound, Copy } from "lucide-react";
import WhatsappSetupWizard from "@/pages/WhatsappSetupWizard";
import { WhatsappStatusPill } from "@/pages/WhatsappSettings";

/**
 * Superadmin-only advanced WhatsApp configuration for a specific org.
 * Route: /admin/organizations/:orgId/whatsapp/manual
 *
 * Session A: this is the ONLY UI that exposes the manual technical wizard
 * (WABA ID, Phone Number ID, tokens). Tenant admins can no longer reach it.
 * Values already saved for an org appear here as `has_access_token` etc; the
 * actual secret values are NEVER shown or returned by the API.
 */
export default function SuperadminOrgWhatsapp() {
  const { orgId } = useParams();
  const [org, setOrg] = useState(null);
  const [wa, setWa] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [wizardOpen, setWizardOpen] = useState(false);

  const refresh = async () => {
    setLoading(true); setErr("");
    try {
      const [{ data: orgs }, { data: waResp }] = await Promise.all([
        api.get("/admin/organizations"),
        api.get(`/admin/organizations/${orgId}/whatsapp-settings`).catch(() => ({ data: null })),
      ]);
      const o = (orgs || []).find((x) => x.id === orgId);
      setOrg(o);
      setWa(waResp);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || "Falha ao carregar dados.");
    } finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, [orgId]);

  if (loading) return <div className="text-sm text-zinc-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> A carregar…</div>;
  if (!org) return <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">Organização não encontrada.</div>;

  return (
    <div className="space-y-6 max-w-3xl" data-testid="superadmin-org-whatsapp">
      <div>
        <Link to="/admin/organizations" className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-900">
          <ArrowLeft className="w-3 h-3" /> Organizações
        </Link>
        <h1 className="mt-2 text-3xl font-display font-bold text-zinc-950">WhatsApp — {org.name}</h1>
        <p className="text-sm text-zinc-500 mt-1">Configuração avançada / manual. Acesso restrito a superadmin.</p>
      </div>

      {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{err}</div>}

      <div className="rounded-2xl border border-zinc-200 bg-white p-6 shadow-sm">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-zinc-700" />
            <h2 className="text-sm font-semibold text-zinc-900">Estado técnico</h2>
          </div>
          <WhatsappStatusPill status={wa?.status || "not_configured"} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
          <TechRow label="Estado interno" value={wa?.status || "—"} />
          <TechRow label="Estado público" value={wa?.public_status || "—"} />
          <TechRow label="Connection mode" value={wa?.connection_mode || "—"} />
          <TechRow label="Onboarding mode" value={wa?.onboarding_mode || "—"} />
          <TechRow label="WABA ID" value={wa?.waba_id || "—"} mono />
          <TechRow label="Phone Number ID" value={wa?.phone_number_id || "—"} mono />
          <TechRow label="Business ID" value={wa?.business_id || "—"} mono />
          <TechRow label="App ID" value={wa?.app_id || "—"} mono />
          <TechRow label="Display phone" value={wa?.display_phone_number || "—"} />
          <TechRow label="Verified name" value={wa?.verified_name || "—"} />
          <TechRow label="Quality rating" value={wa?.quality_rating || "—"} />
          <TechRow label="Credentials version" value={String(wa?.credentials_version ?? 0)} />
          <TechRow label="Access token" value={wa?.has_access_token ? "✓ Encriptado em ficheiro" : "— Não configurado"} secret />
          <TechRow label="App secret" value={wa?.has_app_secret ? "✓ Encriptado em ficheiro" : "— Não configurado"} secret />
          <TechRow label="Webhook verify token" value={wa?.has_webhook_verify_token ? "✓ Encriptado em ficheiro" : "— Não configurado"} secret />
        </div>
      </div>

      <div className="rounded-2xl border border-zinc-200 bg-white p-6 shadow-sm space-y-3">
        <div className="flex items-center gap-2">
          <Info className="w-4 h-4 text-zinc-700" />
          <h2 className="text-sm font-semibold text-zinc-900">Configuração manual</h2>
        </div>
        <p className="text-sm text-zinc-600 leading-relaxed">
          Este wizard permite introduzir manualmente as credenciais Meta desta agência (fallback enquanto Embedded Signup não está aprovado).
          Os secrets são encriptados no servidor com <code>WHATSAPP_CREDENTIAL_ENCRYPTION_KEY</code> — os valores nunca são devolvidos ao frontend.
        </p>
        <button
          onClick={() => setWizardOpen(true)}
          data-testid="sa-open-wizard"
          className="inline-flex items-center gap-2 bg-zinc-950 hover:bg-zinc-800 text-white px-4 py-2.5 rounded-lg text-sm font-semibold"
        >
          <PlugZap className="w-4 h-4" /> Abrir wizard manual
        </button>
      </div>

      {wizardOpen && (
        <WhatsappSetupWizard
          orgId={orgId}
          onClose={() => setWizardOpen(false)}
          onDone={() => { setWizardOpen(false); refresh(); }}
        />
      )}

      <MetaWebhookTokenSection orgId={orgId} wa={wa} onRefresh={refresh} />
      {/* Twilio and Z-API intentionally hidden — Meta Cloud API is the sole
          provider. Components kept in the codebase for future re-enable. */}
      <ProviderSelector orgId={orgId} wa={wa} onRefresh={refresh} />
    </div>
  );
}

function TechRow({ label, value, mono, secret }) {
  return (
    <div className="flex flex-col">
      <div className="text-[11px] uppercase tracking-wider text-zinc-400 font-medium">{label}</div>
      <div className={`text-sm ${mono ? "font-mono" : ""} ${secret ? "text-emerald-800" : "text-zinc-900"}`}>{value}</div>
    </div>
  );
}


function MetaWebhookTokenSection({ orgId, wa, onRefresh }) {
  const configured = !!wa?.has_webhook_verify_token;
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [msg, setMsg] = useState({ type: "", text: "" });
  const backend = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  const callbackUrl = `${backend}/api/webhooks/meta/whatsapp`;

  const gen = () => setToken("pc_" + Math.random().toString(36).slice(2, 10) + Math.random().toString(36).slice(2, 10));
  const copy = (t) => { if (navigator.clipboard) navigator.clipboard.writeText(t); };

  const save = async () => {
    setBusy(true); setMsg({ type: "", text: "" }); setResult(null);
    try {
      const body = token.trim() ? { token: token.trim() } : {};
      const r = await api.post(`/admin/organizations/${orgId}/whatsapp/webhook-verify-token`, body);
      setResult({ token: r.data.token });
      setToken("");
      setMsg({ type: "ok", text: "Verify token guardado (encriptado). Copia-o para a Meta agora — não volta a ser mostrado." });
      onRefresh();
    } catch (e) {
      setMsg({ type: "err", text: formatApiError(e.response?.data?.detail) || "Erro ao guardar." });
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border border-zinc-200 bg-white p-6 shadow-sm space-y-4" data-testid="meta-webhook-token-section">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <KeyRound className="w-4 h-4 text-zinc-700" />
          <h2 className="text-sm font-semibold text-zinc-900">Webhook verify token (Meta)</h2>
        </div>
        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${configured ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`} data-testid="meta-verify-token-status">
          {configured ? "configurado" : "não configurado"}
        </span>
      </div>
      <p className="text-sm text-zinc-600 leading-relaxed">
        Define ou roda a palavra-secreta que a Meta usa para verificar o webhook. Tem de ficar <strong>igual</strong> na Meta (App → WhatsApp → Configuration → Webhook → campo <em>Verify token</em>). É guardada encriptada e mostrada aqui uma única vez.
      </p>
      <div className="rounded-lg bg-zinc-100 border border-zinc-200 px-3 py-2 text-xs font-mono break-all flex items-center justify-between gap-2">
        <span><span className="text-zinc-500 font-sans">Callback URL: </span>{callbackUrl}</span>
        <button type="button" onClick={() => copy(callbackUrl)} data-testid="meta-callback-copy" className="text-zinc-600 hover:text-zinc-900 shrink-0" title="Copiar"><Copy className="w-3.5 h-3.5" /></button>
      </div>
      <div className="flex flex-wrap items-end gap-2">
        <div className="flex-1 min-w-[220px]">
          <Field label="Verify token (deixa vazio para gerar)">
            <input type="text" value={token} onChange={(e) => setToken(e.target.value)} placeholder="ex.: promocrew_labora_2026" data-testid="meta-verify-token-input" className="w-full rounded-lg border border-zinc-200 px-3 py-2 text-sm font-mono" />
          </Field>
        </div>
        <button type="button" onClick={gen} data-testid="meta-verify-token-gen" className="inline-flex items-center gap-2 border border-zinc-300 hover:bg-zinc-50 text-zinc-800 px-4 py-2.5 rounded-lg text-sm font-semibold">Gerar</button>
        <button onClick={save} disabled={busy} data-testid="meta-verify-token-save" className="inline-flex items-center gap-2 bg-zinc-950 hover:bg-zinc-800 disabled:opacity-50 text-white px-4 py-2.5 rounded-lg text-sm font-semibold">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCcw className="w-4 h-4" />} {configured ? "Rodar / atualizar" : "Definir token"}
        </button>
      </div>
      {result?.token && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2.5 space-y-1.5" data-testid="meta-verify-token-result">
          <div className="text-xs font-semibold text-emerald-800">Copia este verify token para a Meta (não volta a aparecer):</div>
          <div className="flex items-center justify-between gap-2 bg-white border border-emerald-200 rounded-md px-2.5 py-1.5">
            <code className="text-sm font-mono text-zinc-900 break-all">{result.token}</code>
            <button type="button" onClick={() => copy(result.token)} data-testid="meta-verify-token-copy" className="text-emerald-700 hover:text-emerald-900 shrink-0" title="Copiar"><Copy className="w-4 h-4" /></button>
          </div>
        </div>
      )}
      {msg.text && (
        <div className={`text-sm rounded-lg px-3 py-2 border ${msg.type === "ok" ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-red-50 border-red-200 text-red-800"}`} data-testid="meta-verify-token-msg">{msg.text}</div>
      )}
    </div>
  );
}


function TwilioSection({ orgId, wa, onRefresh }) {
  const twilio = wa?.twilio || {};
  const [form, setForm] = useState({
    account_sid: twilio.account_sid || "",
    auth_token: "",
    from_number: (twilio.from_number || "").replace(/^whatsapp:/, ""),
    is_sandbox: twilio.is_sandbox !== false,
    sandbox_keyword: twilio.sandbox_keyword || "",
    display_name: twilio.display_name || "",
  });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState({ type: "", text: "" });

  useEffect(() => {
    setForm({
      account_sid: twilio.account_sid || "",
      auth_token: "",
      from_number: (twilio.from_number || "").replace(/^whatsapp:/, ""),
      is_sandbox: twilio.is_sandbox !== false,
      sandbox_keyword: twilio.sandbox_keyword || "",
      display_name: twilio.display_name || "",
    });
  }, [twilio.account_sid, twilio.from_number, twilio.is_sandbox, twilio.sandbox_keyword, twilio.display_name]);

  const submit = async () => {
    setBusy(true); setMsg({ type: "", text: "" });
    try {
      const payload = {
        account_sid: form.account_sid.trim(),
        from_number: form.from_number.trim() || null,
        is_sandbox: form.is_sandbox,
        sandbox_keyword: form.sandbox_keyword || null,
        display_name: form.display_name || null,
      };
      // Only send auth_token when the operator typed a new one
      if (form.auth_token) payload.auth_token = form.auth_token;
      await api.put(`/admin/organizations/${orgId}/twilio-whatsapp`, payload);
      setMsg({ type: "ok", text: "Twilio configurado com sucesso." });
      setForm((f) => ({ ...f, auth_token: "" }));
      onRefresh();
    } catch (e) {
      setMsg({ type: "err", text: formatApiError(e.response?.data?.detail) || "Erro ao guardar." });
    } finally { setBusy(false); }
  };

  const disable = async () => {
    if (!window.confirm("Desligar Twilio para esta agência? As credenciais ficam preservadas.")) return;
    setBusy(true); setMsg({ type: "", text: "" });
    try {
      await api.post(`/admin/organizations/${orgId}/twilio-whatsapp/disable`);
      setMsg({ type: "ok", text: "Twilio desligado." });
      onRefresh();
    } catch (e) {
      setMsg({ type: "err", text: formatApiError(e.response?.data?.detail) || "Erro ao desligar." });
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border border-zinc-200 bg-white p-6 shadow-sm space-y-4" data-testid="twilio-config-section">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <PlugZap className="w-4 h-4 text-zinc-700" />
          <h2 className="text-sm font-semibold text-zinc-900">Twilio WhatsApp (BSP alternativo)</h2>
        </div>
        <span
          className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${
            twilio.status === "connected" ? "bg-emerald-100 text-emerald-800" :
            twilio.status === "disabled" ? "bg-zinc-200 text-zinc-700" :
            twilio.status === "error" ? "bg-red-100 text-red-800" :
            "bg-amber-100 text-amber-800"
          }`}
          data-testid="twilio-status-pill"
        >
          {twilio.status || "not_configured"}
        </span>
      </div>

      <p className="text-sm text-zinc-600 leading-relaxed">
        Alternativa ao Meta Cloud API directo enquanto aguardas Business Verification / App Review Meta.
        As credenciais Twilio ficam encriptadas com a mesma chave Fernet dos secrets Meta e nunca são devolvidas em respostas.
        {form.is_sandbox && (
          <> Em modo <strong>Sandbox</strong> cada destinatário tem de enviar <code>join {form.sandbox_keyword || "&lt;keyword&gt;"}</code> ao número Twilio antes de receber mensagens.</>
        )}
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Account SID" required>
          <input
            type="text"
            value={form.account_sid}
            onChange={(e) => setForm({ ...form, account_sid: e.target.value })}
            placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
            data-testid="twilio-account-sid"
            className="w-full rounded-lg border border-zinc-200 px-3 py-2 text-sm font-mono"
          />
        </Field>
        <Field label={`Auth token${twilio.has_auth_token ? " (já encriptado — só preenche para substituir)" : ""}`}>
          <input
            type="password"
            value={form.auth_token}
            onChange={(e) => setForm({ ...form, auth_token: e.target.value })}
            placeholder={twilio.has_auth_token ? "•••• (preserva o actual)" : "auth token secreto"}
            autoComplete="new-password"
            data-testid="twilio-auth-token"
            className="w-full rounded-lg border border-zinc-200 px-3 py-2 text-sm font-mono"
          />
        </Field>
        <Field label="From number (E.164, sem prefixo)">
          <input
            type="text"
            value={form.from_number}
            onChange={(e) => setForm({ ...form, from_number: e.target.value })}
            placeholder="+14155238886"
            data-testid="twilio-from-number"
            className="w-full rounded-lg border border-zinc-200 px-3 py-2 text-sm font-mono"
          />
        </Field>
        <Field label="Sandbox keyword (info)">
          <input
            type="text"
            value={form.sandbox_keyword}
            onChange={(e) => setForm({ ...form, sandbox_keyword: e.target.value })}
            placeholder="clever-fish"
            data-testid="twilio-sandbox-keyword"
            className="w-full rounded-lg border border-zinc-200 px-3 py-2 text-sm"
          />
        </Field>
        <Field label="Display name">
          <input
            type="text"
            value={form.display_name}
            onChange={(e) => setForm({ ...form, display_name: e.target.value })}
            placeholder="Labora Events"
            data-testid="twilio-display-name"
            className="w-full rounded-lg border border-zinc-200 px-3 py-2 text-sm"
          />
        </Field>
        <label className="flex items-center gap-2 mt-2 sm:mt-6">
          <input
            type="checkbox"
            checked={form.is_sandbox}
            onChange={(e) => setForm({ ...form, is_sandbox: e.target.checked })}
            data-testid="twilio-is-sandbox"
            className="rounded border-zinc-300"
          />
          <span className="text-sm text-zinc-700">Modo Sandbox (desmarca só quando tiveres Sender aprovado)</span>
        </label>
      </div>

      {msg.text && (
        <div className={`text-sm rounded-lg px-3 py-2 border ${
          msg.type === "ok" ? "bg-emerald-50 border-emerald-200 text-emerald-800" :
          "bg-red-50 border-red-200 text-red-800"
        }`} data-testid="twilio-msg">{msg.text}</div>
      )}

      <div className="flex flex-wrap gap-2">
        <button
          onClick={submit}
          disabled={busy || !form.account_sid || (!form.auth_token && !twilio.has_auth_token) || (!form.from_number && !twilio.messaging_service_sid)}
          data-testid="twilio-save-btn"
          className="inline-flex items-center gap-2 bg-zinc-950 hover:bg-zinc-800 disabled:opacity-50 disabled:cursor-not-allowed text-white px-4 py-2.5 rounded-lg text-sm font-semibold"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlugZap className="w-4 h-4" />}
          Guardar Twilio
        </button>
        {twilio.status !== "not_configured" && twilio.status !== "disabled" && (
          <button
            onClick={disable}
            disabled={busy}
            data-testid="twilio-disable-btn"
            className="inline-flex items-center gap-2 border border-zinc-300 hover:bg-zinc-50 text-zinc-800 px-4 py-2.5 rounded-lg text-sm font-semibold"
          >
            <PowerOff className="w-4 h-4" /> Desligar Twilio
          </button>
        )}
      </div>
    </div>
  );
}


function ProviderSelector({ orgId, wa, onRefresh }) {
  const active = wa?.whatsapp_provider || "meta_cloud";
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const select = async (provider) => {
    if (provider === active) return;
    setBusy(true); setMsg("");
    try {
      await api.put(`/admin/organizations/${orgId}/whatsapp-provider`, { provider });
      onRefresh();
    } catch (e) {
      setMsg(formatApiError(e.response?.data?.detail) || "Erro ao mudar provider.");
    } finally { setBusy(false); }
  };

  const twilioReady = wa?.twilio?.status === "connected";
  const metaReady = wa?.status === "connected";
  const zapiReady = wa?.z_api?.status === "connected";
  void twilioReady; void zapiReady;

  const Tile = ({ id, title, desc, ready, testid }) => (
    <button
      onClick={() => select(id)}
      disabled={busy}
      data-testid={testid}
      className={`text-left rounded-xl border-2 p-4 transition ${
        active === id ? "border-zinc-900 bg-zinc-50" : "border-zinc-200 hover:border-zinc-300"
      }`}
    >
      <div className="flex items-center justify-between">
        <div className="text-sm font-semibold text-zinc-900">{title}</div>
        {active === id && (
          <span className="text-[11px] font-semibold bg-emerald-100 text-emerald-800 rounded-full px-2 py-0.5">Activo</span>
        )}
      </div>
      <div className="text-xs text-zinc-500 mt-1">{desc} {ready && <span className="text-emerald-700">✓</span>}</div>
    </button>
  );

  return (
    <div className="rounded-2xl border border-zinc-200 bg-white p-6 shadow-sm space-y-4" data-testid="provider-selector-section">
      <div className="flex items-center gap-2">
        <RefreshCw className="w-4 h-4 text-zinc-700" />
        <h2 className="text-sm font-semibold text-zinc-900">Provider WhatsApp activo</h2>
      </div>
      <p className="text-sm text-zinc-600">
        A plataforma usa <strong>Meta Cloud API</strong> como único provider WhatsApp.
      </p>
      <div className="grid grid-cols-1 gap-3">
        <Tile id="meta_cloud" title="Meta Cloud API" testid="provider-select-meta"
              ready={metaReady}
              desc="Directo com Meta (oficial). Business verificado + App aprovado." />
      </div>
      {msg && <div className="text-sm text-red-800 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{msg}</div>}
    </div>
  );
}


function ZapiSection({ orgId, wa, onRefresh }) {
  const z = wa?.z_api || {};
  const [form, setForm] = useState({
    instance_id: z.instance_id || "",
    instance_token: "",
    client_token: "",
    display_name: z.display_name || "",
  });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState({ type: "", text: "" });
  const [rotating, setRotating] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState("");

  useEffect(() => {
    setForm((f) => ({
      ...f,
      instance_id: z.instance_id || "",
      display_name: z.display_name || "",
    }));
  }, [z.instance_id, z.display_name]);

  const submit = async () => {
    setBusy(true); setMsg({ type: "", text: "" });
    try {
      const payload = { instance_id: form.instance_id.trim() };
      if (form.instance_token) payload.instance_token = form.instance_token;
      if (form.client_token) payload.client_token = form.client_token;
      if (form.display_name !== (z.display_name || "")) payload.display_name = form.display_name;
      await api.put(`/admin/organizations/${orgId}/z-api`, payload);
      setMsg({ type: "ok", text: "Z-API configurado com sucesso. Webhooks Z-API registados." });
      setForm((f) => ({ ...f, instance_token: "", client_token: "" }));
      onRefresh();
    } catch (e) {
      setMsg({ type: "err", text: formatApiError(e.response?.data?.detail) || "Erro ao guardar." });
    } finally { setBusy(false); }
  };

  const disable = async () => {
    if (!window.confirm("Desligar Z-API para esta agência? As credenciais ficam preservadas.")) return;
    setBusy(true); setMsg({ type: "", text: "" });
    try {
      await api.post(`/admin/organizations/${orgId}/z-api/disable`);
      setMsg({ type: "ok", text: "Z-API desligado." });
      onRefresh();
    } catch (e) {
      setMsg({ type: "err", text: formatApiError(e.response?.data?.detail) || "Erro ao desligar." });
    } finally { setBusy(false); }
  };

  const rotate = async () => {
    if (!window.confirm("Rodar webhook token? O URL antigo deixa de funcionar imediatamente.")) return;
    setRotating(true); setMsg({ type: "", text: "" });
    try {
      const r = await api.post(`/admin/organizations/${orgId}/z-api/rotate-webhook-token`);
      setWebhookUrl(r.data.webhook_url || "");
      setMsg({ type: "ok", text: "Webhook token rodado e re-registado no Z-API." });
      onRefresh();
    } catch (e) {
      setMsg({ type: "err", text: formatApiError(e.response?.data?.detail) || "Erro ao rodar token." });
    } finally { setRotating(false); }
  };

  const badgeCls = {
    connected: "bg-emerald-100 text-emerald-800",
    disabled: "bg-zinc-200 text-zinc-700",
    error: "bg-red-100 text-red-800",
    waiting_for_qr: "bg-amber-100 text-amber-800",
    connecting: "bg-amber-100 text-amber-800",
  }[z.status] || "bg-amber-100 text-amber-800";

  return (
    <div className="rounded-2xl border border-zinc-200 bg-white p-6 shadow-sm space-y-4" data-testid="zapi-config-section">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <QrCode className="w-4 h-4 text-zinc-700" />
          <h2 className="text-sm font-semibold text-zinc-900">Z-API (WhatsApp via QR — trials/demos)</h2>
        </div>
        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${badgeCls}`}
              data-testid="zapi-status-pill">
          {z.status || "not_configured"}
        </span>
      </div>

      <div className="rounded-lg border border-amber-200 bg-amber-50 text-amber-900 text-xs px-3 py-2 leading-relaxed">
        <strong>Nota:</strong> Z-API usa WhatsApp Web (não oficial Meta). Existe risco de banimento do número.
        Usar apenas para onboarding rápido, trials e demos.
      </div>

      <p className="text-sm text-zinc-600 leading-relaxed">
        Tokens ficam encriptados com Fernet e nunca são devolvidos em respostas.
        O <code>webhook_token</code> é gerado automaticamente e configurado no Z-API.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Instance ID" required>
          <input
            type="text"
            value={form.instance_id}
            onChange={(e) => setForm({ ...form, instance_id: e.target.value })}
            placeholder="3E1234567890ABCDEF"
            data-testid="zapi-instance-id"
            className="w-full rounded-lg border border-zinc-200 px-3 py-2 text-sm font-mono"
          />
        </Field>
        <Field label={`Instance Token${z.has_instance_token ? " (já encriptado — só preenche p/ substituir)" : ""}`}>
          <input
            type="password"
            value={form.instance_token}
            onChange={(e) => setForm({ ...form, instance_token: e.target.value })}
            placeholder={z.has_instance_token ? "•••• (preserva o actual)" : "instance token"}
            autoComplete="new-password"
            data-testid="zapi-instance-token"
            className="w-full rounded-lg border border-zinc-200 px-3 py-2 text-sm font-mono"
          />
        </Field>
        <Field label={`Client Token${z.has_client_token ? " (já encriptado)" : ""}`}>
          <input
            type="password"
            value={form.client_token}
            onChange={(e) => setForm({ ...form, client_token: e.target.value })}
            placeholder={z.has_client_token ? "•••• (preserva o actual)" : "client security token"}
            autoComplete="new-password"
            data-testid="zapi-client-token"
            className="w-full rounded-lg border border-zinc-200 px-3 py-2 text-sm font-mono"
          />
        </Field>
        <Field label="Display name (info)">
          <input
            type="text"
            value={form.display_name}
            onChange={(e) => setForm({ ...form, display_name: e.target.value })}
            placeholder="Labora Events"
            data-testid="zapi-display-name"
            className="w-full rounded-lg border border-zinc-200 px-3 py-2 text-sm"
          />
        </Field>
      </div>

      {webhookUrl && (
        <div className="text-xs bg-zinc-100 border border-zinc-200 rounded-lg px-3 py-2 font-mono break-all">
          Webhook URL (nunca partilhes): {webhookUrl}
        </div>
      )}

      {msg.text && (
        <div className={`text-sm rounded-lg px-3 py-2 border ${
          msg.type === "ok" ? "bg-emerald-50 border-emerald-200 text-emerald-800" :
          "bg-red-50 border-red-200 text-red-800"
        }`} data-testid="zapi-msg">{msg.text}</div>
      )}

      <div className="flex flex-wrap gap-2">
        <button
          onClick={submit}
          disabled={busy || !form.instance_id || (!form.instance_token && !z.has_instance_token) || (!form.client_token && !z.has_client_token)}
          data-testid="zapi-save-btn"
          className="inline-flex items-center gap-2 bg-zinc-950 hover:bg-zinc-800 disabled:opacity-50 text-white px-4 py-2.5 rounded-lg text-sm font-semibold"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <QrCode className="w-4 h-4" />}
          Guardar Z-API
        </button>
        {z.has_webhook_token && (
          <button
            onClick={rotate}
            disabled={rotating}
            data-testid="zapi-rotate-btn"
            className="inline-flex items-center gap-2 border border-zinc-300 hover:bg-zinc-50 text-zinc-800 px-4 py-2.5 rounded-lg text-sm font-semibold"
          >
            <RefreshCcw className={`w-4 h-4 ${rotating ? "animate-spin" : ""}`} /> Rodar webhook token
          </button>
        )}
        {z.status !== "not_configured" && z.status !== "disabled" && (
          <button
            onClick={disable}
            disabled={busy}
            data-testid="zapi-disable-btn"
            className="inline-flex items-center gap-2 border border-zinc-300 hover:bg-zinc-50 text-zinc-800 px-4 py-2.5 rounded-lg text-sm font-semibold"
          >
            <PowerOff className="w-4 h-4" /> Desligar Z-API
          </button>
        )}
      </div>
    </div>
  );
}


function Field({ label, required, children }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[11px] uppercase tracking-wider text-zinc-500 font-medium">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </span>
      {children}
    </label>
  );
}
